import { ArrowLeft, CheckCircle, Eye, EyeOff, MoreVertical, Pencil, Plus, RefreshCw, Server, Trash } from "lucide-react";
import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { ScopeGate } from "src/components/ScopeGate";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import {
  buildMcpCredentials,
  createHeaderEntry,
  type HeaderEntryType,
  redirectToOAuth,
  useMcpOAuthCallback,
} from "src/pages/SettingsPage/routes/mcp-server-utils";

import type { McpServersOnboardingContentCreateMutation } from "./__generated__/McpServersOnboardingContentCreateMutation.graphql";
import type { McpServersOnboardingContentDeleteMutation } from "./__generated__/McpServersOnboardingContentDeleteMutation.graphql";
import type { McpServersOnboardingContentQuery, McpServersOnboardingContentQuery$data } from "./__generated__/McpServersOnboardingContentQuery.graphql";
import type { McpServersOnboardingContentTestConnectionMutation } from "./__generated__/McpServersOnboardingContentTestConnectionMutation.graphql";
import type { McpServersOnboardingContentTestOverridesMutation } from "./__generated__/McpServersOnboardingContentTestOverridesMutation.graphql";
import type { McpServersOnboardingContentTestUrlMutation } from "./__generated__/McpServersOnboardingContentTestUrlMutation.graphql";
import type { McpServersOnboardingContentUpdateMutation } from "./__generated__/McpServersOnboardingContentUpdateMutation.graphql";

const mcpServersQuery = graphql`
  query McpServersOnboardingContentQuery {
    customMcpServers {
      id
      name
      description
      url
      authType
      isActive
      toolCount
    }
  }
`;

const createServerMutation = graphql`
  mutation McpServersOnboardingContentCreateMutation($input: CreateCustomMcpServerInput!) {
    createCustomMcpServer(input: $input) {
      server { id name }
      error
      oauthUrl
    }
  }
`;

const testUrlMutation = graphql`
  mutation McpServersOnboardingContentTestUrlMutation($input: TestCustomMcpServerUrlInput!) {
    testCustomMcpServerUrl(input: $input) {
      success
      tools
      error
    }
  }
`;

const updateServerMutation = graphql`
  mutation McpServersOnboardingContentUpdateMutation($input: UpdateCustomMcpServerInput!) {
    updateCustomMcpServer(input: $input) {
      server { id name }
      error
    }
  }
`;

const testOverridesMutation = graphql`
  mutation McpServersOnboardingContentTestOverridesMutation($input: TestCustomMcpServerOverridesInput!) {
    testCustomMcpServerOverrides(input: $input) {
      success
      tools
      error
    }
  }
`;

const testConnectionMutation = graphql`
  mutation McpServersOnboardingContentTestConnectionMutation($id: GlobalID!) {
    testCustomMcpServerConnection(id: $id) {
      success
      tools
      error
      oauthUrl
    }
  }
`;

const deleteServerMutation = graphql`
  mutation McpServersOnboardingContentDeleteMutation($id: GlobalID!) {
    deleteCustomMcpServer(id: $id) {
      server { id }
      error
    }
  }
`;

type McpServerType = NonNullable<McpServersOnboardingContentQuery$data["customMcpServers"]>[number];

interface McpServerFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  server?: McpServerType | null;
}

function McpServerFormDialog({ isOpen, onClose, onSuccess, server }: McpServerFormDialogProps) {
  const isEdit = !!server;
  const { toast } = useToast();

  const [commitCreate, isCreating] = useMutation<McpServersOnboardingContentCreateMutation>(createServerMutation);
  const [commitTestUrl] = useMutation<McpServersOnboardingContentTestUrlMutation>(testUrlMutation);
  const [commitUpdate, isUpdating] = useMutation<McpServersOnboardingContentUpdateMutation>(updateServerMutation);
  const [commitTestConnection] = useMutation<McpServersOnboardingContentTestConnectionMutation>(testConnectionMutation);
  const [commitTestOverrides] = useMutation<McpServersOnboardingContentTestOverridesMutation>(testOverridesMutation);

  const [name, setName] = useState(server?.name ?? "");
  const [description, setDescription] = useState(server?.description ?? "");
  const [url, setUrl] = useState(server?.url ?? "");
  const [authType, setAuthType] = useState(server?.authType ?? "API_KEY");
  const [apiKey, setApiKey] = useState("");
  const [headers, setHeaders] = useState<HeaderEntryType[]>([createHeaderEntry()]);
  const [showSecrets, setShowSecrets] = useState(false);
  const [urlOrAuthChanged, setUrlOrAuthChanged] = useState(false);
  const [testStatus, setTestStatus] = useState<{
    status: "idle" | "testing" | "success" | "failed";
    tools: readonly string[];
    error: string | null;
  }>({ status: "idle", tools: [], error: null });

  const isTesting = testStatus.status === "testing";
  const isSaving = isCreating || isUpdating;
  const isOAuth = authType === "OAUTH";

  const hasNewCredentials =
    (authType === "API_KEY" && apiKey.trim() !== "") ||
    (authType === "CUSTOM_HEADERS" && headers.some((h) => h.key.trim() || h.value.trim()));
  const connectionChanged = isEdit ? (urlOrAuthChanged || hasNewCredentials) : true;

  const hasIncompleteHeaders =
    authType === "CUSTOM_HEADERS" &&
    headers.some((h) => (h.key.trim() && !h.value.trim()) || (!h.key.trim() && h.value.trim()));
  const hasNoValidHeaders =
    authType === "CUSTOM_HEADERS" && !headers.some((h) => h.key.trim() && h.value.trim());

  const canTest = !!url && !isTesting && !hasIncompleteHeaders && !(authType === "CUSTOM_HEADERS" && hasNoValidHeaders);

  const canSave = isEdit
    ? !!name.trim() && !!description.trim() && !!url.trim() && !hasIncompleteHeaders &&
      (!connectionChanged || isOAuth || testStatus.status === "success")
    : isOAuth
      ? !!name.trim() && !!description.trim() && !!url
      : !!name.trim() && !!description.trim() && !!url && testStatus.status === "success";

  const resetTest = () => setTestStatus({ status: "idle", tools: [], error: null });

  const handleTest = useCallback(() => {
    setTestStatus({ status: "testing", tools: [], error: null });

    if (isEdit && server && !connectionChanged) {
      commitTestConnection({
        variables: { id: server.id },
        onCompleted: (response) => {
          const result = response.testCustomMcpServerConnection;
          if (result?.oauthUrl) {
            redirectToOAuth(result.oauthUrl, toast);
            return;
          }
          if (result?.success) {
            setTestStatus({ status: "success", tools: result.tools ?? [], error: null });
          } else {
            setTestStatus({ status: "failed", tools: [], error: result?.error ?? "Connection failed" });
          }
        },
        onError: (error) => setTestStatus({ status: "failed", tools: [], error: error.message }),
      });
    } else if (isEdit && server) {
      const credentials = buildMcpCredentials(authType, apiKey, headers);
      commitTestOverrides({
        variables: {
          input: { id: server.id, url, authType, credentials: credentials ?? null },
        },
        onCompleted: (response) => {
          const result = response.testCustomMcpServerOverrides;
          if (result?.success) {
            setTestStatus({ status: "success", tools: result.tools ?? [], error: null });
          } else {
            setTestStatus({ status: "failed", tools: [], error: result?.error ?? "Connection failed" });
          }
        },
        onError: (error) => setTestStatus({ status: "failed", tools: [], error: error.message }),
      });
    } else {
      const credentials = buildMcpCredentials(authType, apiKey, headers);
      commitTestUrl({
        variables: { input: { url, authType, credentials: credentials ?? null } },
        onCompleted: (response) => {
          const result = response.testCustomMcpServerUrl;
          if (result?.success) {
            setTestStatus({ status: "success", tools: result.tools ?? [], error: null });
          } else {
            setTestStatus({ status: "failed", tools: [], error: result?.error ?? "Connection failed" });
          }
        },
        onError: (error) => setTestStatus({ status: "failed", tools: [], error: error.message }),
      });
    }
  }, [isEdit, server, connectionChanged, url, authType, apiKey, headers, commitTestConnection, commitTestUrl, commitTestOverrides, toast]);

  const handleSave = useCallback(() => {
    const credentials = buildMcpCredentials(authType, apiKey, headers);

    if (isEdit && server) {
      commitUpdate({
        variables: {
          input: {
            id: server.id,
            name: name.trim(),
            description: description.trim() || null,
            ...(connectionChanged && {
              url,
              authType,
              ...(credentials ? { credentials } : {}),
            }),
          },
        },
        onCompleted: (response) => {
          const result = response.updateCustomMcpServer;
          if (result?.error && !result.server) {
            toast({ title: "Error", description: result.error, variant: "error" });
          } else {
            toast({ title: "Server updated", description: `${name} has been updated.` });
            onSuccess();
          }
        },
        onError: (error) => { toast({ title: "Error", description: error.message, variant: "error" }); },
      });
    } else {
      commitCreate({
        variables: {
          input: {
            name: name.trim(),
            url,
            authType,
            credentials: credentials ?? null,
            description: description.trim() || null,
            returnPath: "/onboarding/integrations/mcp-servers",
          },
        },
        onCompleted: (response) => {
          const result = response.createCustomMcpServer;
          if (result?.oauthUrl) {
            redirectToOAuth(result.oauthUrl, toast);
            return;
          }
          if (result?.error && !result.server) {
            toast({ title: "Error", description: result.error, variant: "error" });
          } else {
            if (result?.error) {
              toast({ title: "Server added", description: result.error, variant: "error" });
            } else {
              toast({ title: "MCP Server added", description: `${name} has been configured.` });
            }
            onSuccess();
          }
        },
        onError: (error) => { toast({ title: "Error", description: error.message, variant: "error" }); },
      });
    }
  }, [isEdit, server, name, description, url, authType, apiKey, headers, connectionChanged, commitCreate, commitUpdate, toast, onSuccess]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-popover text-foreground border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit MCP Server" : "Add MCP Server"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mcp-dialog-name">Server Name <span className="text-red-500">*</span></Label>
              <Input
                id="mcp-dialog-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. My MCP Server"
                disabled={isTesting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mcp-dialog-description">Description <span className="text-red-500">*</span></Label>
              <p className="text-muted-foreground text-xs">
                Describe what this MCP server handles. This is used to decide when to use its tools.
              </p>
              <textarea
                id="mcp-dialog-description"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                placeholder="e.g. Query and analyze PostgreSQL databases"
                rows={3}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          {/* Connection Settings */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mcp-dialog-url">Server URL</Label>
              <Input
                id="mcp-dialog-url"
                type="url"
                autoComplete="off"
                value={url}
                disabled={isTesting}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (isEdit) setUrlOrAuthChanged(true);
                  resetTest();
                }}
                placeholder="https://mcp.example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mcp-dialog-auth-type">Auth Type</Label>
              <Select
                value={authType}
                disabled={isTesting}
                onValueChange={(v) => {
                  setAuthType(v);
                  setApiKey("");
                  setHeaders([createHeaderEntry()]);
                  if (isEdit) setUrlOrAuthChanged(true);
                  resetTest();
                }}
              >
                <SelectTrigger id="mcp-dialog-auth-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="API_KEY">Bearer Token</SelectItem>
                  <SelectItem value="CUSTOM_HEADERS">Custom Headers</SelectItem>
                  <SelectItem value="OAUTH">OAuth</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {authType === "API_KEY" && (
              <div className="space-y-2">
                <Label htmlFor="mcp-dialog-api-key">Bearer Token</Label>
                <SecretInput
                  id="mcp-dialog-api-key"
                  placeholder={isEdit ? "Leave blank to keep current token" : "Enter bearer token"}
                  value={apiKey}
                  disabled={isTesting}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    resetTest();
                  }}
                />
              </div>
            )}

            {authType === "CUSTOM_HEADERS" && (
              <div className="space-y-2" role="group" aria-labelledby="mcp-dialog-headers-label">
                <Label id="mcp-dialog-headers-label">Headers</Label>
                {isEdit && <p className="text-xs text-muted-foreground">Leave blank to keep current headers.</p>}
                {headers.map((header, idx) => (
                  <div key={header.id} className="flex gap-2">
                    <input
                      type="text"
                      autoComplete="off"
                      className="w-2/5 rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
                      aria-label={`Header ${idx + 1} name`}
                      placeholder="Header name"
                      value={header.key}
                      disabled={isTesting}
                      onChange={(e) => {
                        setHeaders((h) => h.map((entry, i) => (i === idx ? { ...entry, key: e.target.value } : entry)));
                        resetTest();
                      }}
                    />
                    <div className="relative flex-1">
                      <input
                        type={showSecrets ? "text" : "password"}
                        autoComplete="off"
                        className="w-full rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm disabled:opacity-50"
                        aria-label={`Header ${idx + 1} value`}
                        placeholder="Value"
                        value={header.value}
                        disabled={isTesting}
                        onChange={(e) => {
                          setHeaders((h) => h.map((entry, i) => (i === idx ? { ...entry, value: e.target.value } : entry)));
                          resetTest();
                        }}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowSecrets((s) => !s)}
                        tabIndex={-1}
                      >
                        {showSecrets ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                      </button>
                    </div>
                    {headers.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-2"
                        disabled={isTesting}
                        onClick={() => {
                          setHeaders((h) => h.filter((_, i) => i !== idx));
                          resetTest();
                        }}
                      >
                        <Trash className="size-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isTesting}
                  onClick={() => setHeaders((h) => [...h, createHeaderEntry()])}
                >
                  <Plus className="size-3.5 mr-1" />
                  Add Header
                </Button>
                {hasIncompleteHeaders && (
                  <p className="text-xs text-destructive">Each header must have both a name and a value.</p>
                )}
                {!isEdit && !hasIncompleteHeaders && hasNoValidHeaders && (
                  <p className="text-xs text-destructive">At least one header is required.</p>
                )}
              </div>
            )}

            {isOAuth && (
              <p className="text-xs text-muted-foreground">
                {isEdit && connectionChanged
                  ? "Saving will restart the OAuth flow. You'll be redirected to the provider."
                  : "OAuth authorization will begin after adding the server. You'll be redirected to complete setup."}
              </p>
            )}
          </div>

          {/* Test result */}
          {!isOAuth && connectionChanged && testStatus.status !== "success" && (
            <p className="text-xs text-yellow-500">
              {isEdit ? "Connection settings changed. Test Connection is required before saving." : "Test Connection is required before adding a server."}
            </p>
          )}
          {testStatus.status === "success" && (
            <div className="flex items-center gap-2 rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2">
              <CheckCircle className="size-4 text-green-500 flex-shrink-0" />
              <span className="text-sm text-green-500">
                Connected — {testStatus.tools.length} tool{testStatus.tools.length !== 1 ? "s" : ""} discovered
              </span>
            </div>
          )}
          {testStatus.status === "failed" && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
              <p className="text-sm text-destructive">{testStatus.error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {!isOAuth && (
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={!canTest || isTesting}
            >
              <RefreshCw className={`size-3.5 mr-1.5 ${isTesting ? "animate-spin" : ""}`} />
              {isTesting ? "Testing..." : "Test Connection"}
            </Button>
          )}
          <Button onClick={handleSave} disabled={isSaving || !canSave} loading={isSaving}>
            {isEdit ? "Save Changes" : "Add Server"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface McpServersOnboardingContentProps {
  onRefetch: () => void;
}

export default function McpServersOnboardingContent({ onRefetch }: McpServersOnboardingContentProps) {
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const { toast } = useToast();

  useMcpOAuthCallback(toast);

  const [fetchKey, setFetchKey] = useState(0);
  const data = useLazyLoadQuery<McpServersOnboardingContentQuery>(
    mcpServersQuery,
    {},
    { fetchPolicy: "network-only", fetchKey },
  );

  const [commitDelete, isDeleting] = useMutation<McpServersOnboardingContentDeleteMutation>(deleteServerMutation);

  const servers = data.customMcpServers ?? [];

  const [formDialog, setFormDialog] = useState<{ isOpen: boolean; server?: McpServerType | null }>({
    isOpen: false,
    server: null,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; serverId: string | null; serverName: string }>({
    isOpen: false,
    serverId: null,
    serverName: "",
  });

  const refetch = useCallback(() => {
    setFetchKey((k) => k + 1);
    onRefetch();
  }, [onRefetch]);

  const handleFormSuccess = useCallback(() => {
    setFormDialog({ isOpen: false, server: null });
    refetch();
  }, [refetch]);

  const handleDelete = useCallback(() => {
    if (!deleteConfirm.serverId) return;
    commitDelete({
      variables: { id: deleteConfirm.serverId },
      onCompleted: () => {
        toast({ title: "Deleted", description: `${deleteConfirm.serverName} has been removed.` });
        setDeleteConfirm({ isOpen: false, serverId: null, serverName: "" });
        refetch();
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "error" });
      },
    });
  }, [deleteConfirm, commitDelete, toast, refetch]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/onboarding")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Integrations
        </button>

        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-foreground">Custom MCP Servers</h2>
          {servers.length < 5 && (
            <ScopeGate scope="write:integrations">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFormDialog({ isOpen: true, server: null })}
              >
                <Plus className="size-3.5 mr-1.5" />
                Add Server
              </Button>
            </ScopeGate>
          )}
        </div>
        <p className="text-muted-foreground text-sm mt-2">
          Connect external MCP servers to extend your agent with custom tools.
        </p>
      </div>

      {/* Server List */}
      {servers.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <Server className="mx-auto size-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No custom MCP servers configured</p>
        </div>
      ) : (
        <div className="space-y-2">
          {servers.map((server) => (
            <div
              key={server.id}
              className="flex items-center justify-between rounded-lg border border-border bg-card p-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`size-2 rounded-full flex-shrink-0 ${server.isActive ? "bg-green-500" : "bg-yellow-500"}`}
                />
                <div className="min-w-0">
                  <span className="text-sm font-medium text-foreground truncate">{server.name}</span>
                  {server.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[400px]">
                      {server.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span className="truncate max-w-[300px]">{server.url}</span>
                    <span>·</span>
                    <span>{server.toolCount} tool{server.toolCount !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              </div>

              {canModify && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="size-8 p-0">
                      <MoreVertical className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setFormDialog({ isOpen: true, server })}
                    >
                      <Pencil className="size-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() =>
                        setDeleteConfirm({ isOpen: true, serverId: server.id, serverName: server.name })
                      }
                    >
                      <Trash className="size-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      {formDialog.isOpen && (
        <McpServerFormDialog
          isOpen={formDialog.isOpen}
          onClose={() => setFormDialog({ isOpen: false, server: null })}
          onSuccess={handleFormSuccess}
          server={formDialog.server}
        />
      )}

      {/* Delete Confirmation */}
      <UIConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, serverId: null, serverName: "" })}
        onConfirm={handleDelete}
        title="Delete MCP Server"
        description={`Are you sure you want to delete "${deleteConfirm.serverName}"? The agent will no longer have access to its tools.`}
        confirmText="Delete"
        variant="destructive"
        isLoading={isDeleting}
      />
    </div>
  );
}
